import React, {memo, useMemo, useRef, useState} from 'react';
import useOutsideClick from 'hooks/useOutsideClick';
import PropTypes from 'prop-types';
import moment from 'moment';
import cn from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import IconDollarType from 'assets/iconDollar';
import IconPercentType from 'assets/iconPercent';
import IconSort from 'assets/icon-sort.svg';
import PopperActions from 'components/PopperActions';
import TableComponent from 'components/TableComponent';
import {setPopperActions} from 'routes/App/discountsV2/helpers';
import {
  DISCOUNT_STATUS_FILTER,
  DISCOUNT_STATUS_FILTER_LABEL,
  DISCOUNT_TYPE,
  DISCOUNT_V2
} from 'helpers/constants/discountsV2';
import {FlexContainer, FlexItem} from 'components/layout';
import DiscountsTableWrapper, {
  StatusHeaderDropdown,
  StatusHeaderMenu,
  StatusHeaderMenuItem,
  StatusHeaderTrigger
} from 'routes/App/discountsV2/DiscountsList/DiscountsTable/styles';
import {StatusBadge} from 'routes/App/discountsV2/DiscountsList/DiscountsToolbar/styles';
import {isEmailMarketingTrialExpired} from 'helpers/functionUtils/emailMarketing';

const DiscountsTable = (props) => {
  const {
    discounts,
    loading,
    onSort,
    discountDirection,
    orderBy = DISCOUNT_V2.NAME,
    popperActions,
    allowedServiceFeatures,
    emailMarketingTrialDate,
    timezone,
    statistics,
    status,
    onStatusChange
  } = props;

  const ordersByDiscountId = useMemo(() => {
    if (!statistics?.discounts) return {};
    return statistics.discounts.reduce((acc, item) => {
      if (item?.id) acc[item.id] = item.ordersCount || 0;
      return acc;
    }, {});
  }, [statistics]);

  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  useOutsideClick(
    statusDropdownRef,
    () => setIsStatusMenuOpen(false),
    isStatusMenuOpen
  );

  const handleStatusSelect = (next) => {
    setIsStatusMenuOpen(false);
    if (onStatusChange) onStatusChange(next);
  };

  const statusHeader = (
    <StatusHeaderDropdown ref={statusDropdownRef}>
      <StatusHeaderTrigger
        type='button'
        onClick={() => setIsStatusMenuOpen((prev) => !prev)}
        aria-expanded={isStatusMenuOpen}
      >
        {DISCOUNT_STATUS_FILTER_LABEL[status] ||
          DISCOUNT_STATUS_FILTER_LABEL[DISCOUNT_STATUS_FILTER.ACTIVE]}
        <img src={IconSort} alt='' />
      </StatusHeaderTrigger>
      {isStatusMenuOpen && (
        <StatusHeaderMenu>
          {Object.values(DISCOUNT_STATUS_FILTER).map((value) => (
            <StatusHeaderMenuItem
              key={value}
              type='button'
              className={cn({isActive: value === status})}
              onClick={() => handleStatusSelect(value)}
            >
              {DISCOUNT_STATUS_FILTER_LABEL[value]}
            </StatusHeaderMenuItem>
          ))}
        </StatusHeaderMenu>
      )}
    </StatusHeaderDropdown>
  );

  const getFieldsConfig = () => [
    {
      title: 'Name',
      fieldName: DISCOUNT_V2.NAME,
      sortable: true,
      onCellClick: (discount) => popperActions.editAction(discount),
      renderer: (data) => (
        <FlexContainer
          className='align-center'
          style={{minWidth: 0, overflow: 'hidden'}}
        >
          <FlexItem
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0
            }}
            title={data[DISCOUNT_V2.NAME]}
          >
            {data[DISCOUNT_V2.NAME]}
          </FlexItem>
        </FlexContainer>
      )
    },
    {
      title: 'Type',
      fieldName: DISCOUNT_V2.TYPE,
      sortable: true,
      onCellClick: (discount) => popperActions.editAction(discount),
      renderer: (data) => (
        <div className='IconDiscounts'>
          {data?.type === DISCOUNT_TYPE.WHOLE_CART &&
            data?.rate?.type === DISCOUNT_TYPE.PERCENTAGE && (
              <div className='IconPercentType'>
                <IconPercentType />
              </div>
            )}
          {data?.type === DISCOUNT_TYPE.WHOLE_CART &&
            data?.rate?.type === DISCOUNT_TYPE.FIXED && (
              <div className='IconDollarType'>
                <IconDollarType />
              </div>
            )}
          {data?.type === DISCOUNT_TYPE.BOGO && (
            <div
              className='IconBogoType'
              style={{fontWeight: 700, color: '#929cb9'}}
            >
              BOGO
            </div>
          )}
          {data?.type === DISCOUNT_TYPE.SPECIFIC_ITEMS && (
            <div
              className='IconSpecificItems'
              style={{
                fontWeight: 700,
                color: '#929cb9',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              ITEM
              {data?.rate?.type === DISCOUNT_TYPE.FIXED && <IconDollarType />}
              {data?.rate?.type === DISCOUNT_TYPE.PERCENTAGE && (
                <IconPercentType />
              )}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Value',
      fieldName: DISCOUNT_V2.DISCOUNT_VALUE,
      sortable: true,
      onCellClick: (discount) => popperActions.editAction(discount),
      renderer: (data) => (
        <>{data?.type === DISCOUNT_TYPE.BOGO ? '' : data?.rate?.value}</>
      )
    },
    {
      title: 'Min Order',
      fieldName: DISCOUNT_V2.MIN_ORDER,
      sortable: true,
      onCellClick: (discount) => popperActions.editAction(discount),
      renderer: (data) => (
        <>{data?.subtotal?.min ? `$ ${data.subtotal.min}` : ''}</>
      )
    },
    {
      title: 'Promo Code',
      fieldName: DISCOUNT_V2.MIN_ORDER,
      sortable: false,
      onCellClick: (discount) => popperActions.editAction(discount),
      renderer: (data) => <>{data?.promoCode?.code || ''}</>
    },
    ...(allowedServiceFeatures.includes('email_campaigns_active') ||
    (emailMarketingTrialDate &&
      !isEmailMarketingTrialExpired(emailMarketingTrialDate, timezone))
      ? [
          {
            title: 'Email marketing',
            fieldName: DISCOUNT_V2.EMAIL_MARKETING,
            sortable: false,
            onCellClick: (discount) => popperActions.editAction(discount),
            renderer: (data) => (
              <div className='greenText'>
                {data?.marketing?.email?.isEnabled ? 'Enabled' : ''}
              </div>
            )
          }
        ]
      : []),
    {
      title: 'Hidden',
      fieldName: 'isPromotional',
      sortable: false,
      onCellClick: (discount) => popperActions.editAction(discount),
      renderer: (data) => (
        <div style={{color: '#ff2d38', fontWeight: 700}}>
          {data?.isPromotional === false ? 'Yes' : ''}
        </div>
      )
    },
    {
      title: 'Start date',
      fieldName: DISCOUNT_V2.START_DATE,
      sortable: true,
      onCellClick: (discount) => popperActions.editAction(discount),
      renderer: (data) => (
        <>
          {data?.availability?.activatesAtLocal
            ? moment(data.availability.activatesAtLocal).utc().format('MMMM D')
            : 'Always On'}
        </>
      )
    },
    {
      title: 'End date',
      fieldName: DISCOUNT_V2.END_DATE,
      sortable: true,
      onCellClick: (discount) => popperActions.editAction(discount),
      renderer: (data) => (
        <>
          {data?.availability?.deactivatesAtLocal
            ? moment(data.availability.deactivatesAtLocal)
                .utc()
                .format('MMMM D')
            : 'Always On'}
        </>
      )
    },
    {
      title: 'Orders',
      fieldName: 'orders',
      sortable: false,
      renderer: (data) => {
        if (!statistics) return <span style={{color: '#929cb9'}}>—</span>;
        return ordersByDiscountId[data?.id] || 0;
      }
    },
    {
      title: statusHeader,
      fieldName: 'status',
      sortable: false,
      renderer: (data) => (
        <StatusBadge
          className={cn({
            active: data?.status === 'Active',
            archived: data?.status !== 'Active'
          })}
        >
          {data?.status === 'Active' ? 'Active' : 'Archived'}
        </StatusBadge>
      )
    },
    {
      renderer: (data) => {
        return (
          <PopperActions
            popperActions={() =>
              setPopperActions(
                popperActions.editAction,
                popperActions.duplicateAction,
                popperActions.disableAction,
                popperActions.deleteAction,
                data?.status
              )
            }
            row={data}
            disablePortal={false}
          />
        );
      }
    }
  ];

  return (
    <DiscountsTableWrapper className={!discounts.length ? 'empty' : ''}>
      <TableComponent
        fields={getFieldsConfig()}
        data={discounts}
        loading={loading}
        rowKeyPath='id'
        onSort={onSort}
        orderBy={orderBy}
        orderDirection={discountDirection}
      />
    </DiscountsTableWrapper>
  );
};

DiscountsTable.propTypes = {
  orderBy: PropTypes.string,
  loading: PropTypes.bool,
  onSort: PropTypes.func,
  discounts: PropTypes.array.isRequired,
  discountDirection: PropTypes.string,
  popperActions: PropTypes.objectOf({
    editAction: PropTypes.func,
    duplicateAction: PropTypes.func,
    disableAction: PropTypes.func,
    deleteAction: PropTypes.func
  }).isRequired,
  allowedServiceFeatures: PropTypes.array.isRequired,
  emailMarketingTrialDate: PropTypes.string,
  timezone: PropTypes.string.isRequired,
  statistics: PropTypes.shape({
    discounts: PropTypes.array
  }),
  status: PropTypes.string,
  onStatusChange: PropTypes.func
};

DiscountsTable.defaultProps = {
  orderBy: '',
  loading: false,
  onSort: () => {},
  discountDirection: '',
  emailMarketingTrialDate: null,
  statistics: null,
  status: DISCOUNT_STATUS_FILTER.ACTIVE,
  onStatusChange: () => {}
};

export default memo(DiscountsTable);
